import { InputNumber, Select } from "antd";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { convertValue, MEASUREMENT_KEYS } from "../../helpers/unit-conversion";
import { startStatusLoader } from "../../redux/statusCalculation/actions";
import { FETCH_STATUS_CALCULATION_SAGA } from "../../redux/statusCalculation/types";

import StatusConfigurationGrid from "../AgGrid/status-configuration";
import CommonLoader from "../Loaders/loader";

const { Option } = Select;

const PASS_GROUP_ORDER = [
  "root",
  "hot",
  "hot pass",
  "fill",
  "last fill",
  "strip",
  "cap",
];

const getUniqueJobNumbers = (data, weldDetails) => {
  if (!data || data.length === 0) return [];

  return [
    ...new Set(
      data.map((item) => `${item.job_number}:${item.pipe_name ?? ""}`)
    ),
  ]
    .filter((jobNumber) => jobNumber !== null && jobNumber.split(":")[0])
    .map((job_number) => {
      const [jobNum, pipeName = ""] = job_number.split(":");
      const welding_system =
        weldDetails
          ?.find((wld) => wld.job_number == jobNum)
          ?.welding_system?.replace(/\W/g, "") || "";

      return {
        label: `${welding_system}: ${jobNum}${pipeName ? `: ${pipeName}` : ""}`,
        value: job_number,
      };
    });
};

const useStatusConfiguration = () => {
  const dispatch = useDispatch();
  const StatusData = useSelector((state) => state.statusReducer);
  const projectReducer = useSelector((state) => state.projectReducer);

  const { statusList } = StatusData;
  const { allDataSet = {} } = statusList || {};
  const { recordList: tableLimitData = [] } = allDataSet;
  const weldDetails = statusList?.weldDetails || [];

  const selectedProject = projectReducer.selectedProject || {};
  const { project_id: selectedProjectId, project_name: selectedProjectName } =
    selectedProject;

  const [selectedJobNumber, setSelectedJobNumber] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  useEffect(() => {
    if (selectedProjectId !== currentProjectId) {
      setSelectedJobNumber(null);
      setCurrentProjectId(selectedProjectId);
    }

    if (selectedProjectId && selectedProjectName) {
      const params = {
        skip: 0,
        limit: 100,
        project_id: selectedProjectId,
        project_name: selectedProjectName,
      };
      dispatch(startStatusLoader());
      dispatch({
        type: FETCH_STATUS_CALCULATION_SAGA,
        payload: params,
      });
    }
  }, [dispatch, selectedProjectId, selectedProjectName, currentProjectId]);

  useEffect(() => {
    if (selectedProjectId !== currentProjectId) {
      setSelectedJobNumber(null);
    }
  }, [selectedProjectId, currentProjectId]);

  useEffect(() => {
    if (
      tableLimitData?.length > 0 &&
      !selectedJobNumber &&
      selectedProjectId === currentProjectId
    ) {
      const dataForCurrentProject = tableLimitData.some(
        (item) =>
          item.project_id === selectedProjectId ||
          (selectedProject &&
            item.project_name === selectedProject.project_name)
      );

      if (dataForCurrentProject) {
        const uniqueJobNumbers = getUniqueJobNumbers(
          tableLimitData,
          weldDetails
        );
        if (uniqueJobNumbers.length > 0) {
          setSelectedJobNumber(uniqueJobNumbers[0]?.value);
        }
      }
    }
  }, [
    tableLimitData,
    selectedJobNumber,
    weldDetails,
    selectedProjectId,
    currentProjectId,
    selectedProject,
  ]);

  return {
    tableLimitData,
    weldDetails,
    selectedJobNumber,
    setSelectedJobNumber,
    selectedProject,
    projectReducer,
  };
};

const processRecordList = (
  tableLimitData,
  selectedJobNumber,
  selectedWeldDetail
) => {
  if (!tableLimitData || !selectedJobNumber) return [];

  const weldingSystem =
    selectedWeldDetail?.welding_system?.replace(/\W/g, "") || "";

  const [jobNum, pipeName] = selectedJobNumber.split(":");

  const filteredData = tableLimitData.filter(
    (item) =>
      item.job_number === jobNum &&
      (pipeName
        ? item.pipe_name == pipeName
        : item.pipe_name == null || item.pipe_name == "null")
  );

  const transformedData = filteredData.reduce((acc, pass) => {
    const existingPass = acc.find(
      (p) => p.pass_group_name === pass.pass_group_name
    );

    if (existingPass) {
      existingPass.values.push({ ...pass });
    } else {
      acc.push({
        pass_group_name:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? `${pass.pass_name}-${pass.zone_name}`
            : pass.pass_group_name,
        display_name:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? `${pass.pass_name}-${
                weldingSystem == "M500" ? `${pass.m500_zone}` : pass.zone_name
              }`
            : pass.pass_group_name,
        values: [{ ...pass }],
      });
    }
    return acc;
  }, []);

  const sortedData = transformedData.sort((a, b) => {
    const indexA = PASS_GROUP_ORDER.indexOf(a.pass_group_name?.toLowerCase());
    const indexB = PASS_GROUP_ORDER.indexOf(b.pass_group_name?.toLowerCase());

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  const filteredSortedData = sortedData.filter(
    (item) =>
      Boolean(item.pass_group_name) &&
      !/^\d+$/.test(item.pass_name) &&
      !/^\d+$/.test(item.pass_group_name)
  );

  let finalData = [];
  filteredSortedData.forEach((item) => {
    finalData.push({
      ...item,
      pass_group_name: `${item.display_name} (Min)`,
      original_pass_name: item.pass_group_name,
      dataIndex: "min",
      key: "min",
    });
    finalData.push({
      ...item,
      pass_group_name: `${item.display_name} (Max)`,
      original_pass_name: item.pass_group_name,
      dataIndex: "max",
      key: "max",
    });
  });

  return finalData;
};

const StatusConfiguration = () => {
  const {
    tableLimitData,
    weldDetails,
    selectedJobNumber,
    setSelectedJobNumber,
    selectedProject,
    projectReducer,
  } = useStatusConfiguration();
  const StatusData = useSelector((state) => state.statusReducer);

  const getSelectedWeldDetail = useCallback(() => {
    if (!selectedJobNumber || !weldDetails || weldDetails.length === 0) {
      return null;
    }

    const [jobNum, pipeName] = selectedJobNumber.split(":");

    return weldDetails?.find(
      (ite) =>
        ite.job_number === jobNum &&
        (pipeName
          ? ite.pipe_name == pipeName
          : ite.pipe_name == null || ite.pipe_name == "null")
    );
  }, [selectedJobNumber, weldDetails]);

  const selectedWeldDetail = useMemo(getSelectedWeldDetail, [
    getSelectedWeldDetail,
  ]);

  const recordList = useMemo(
    () =>
      processRecordList(tableLimitData, selectedJobNumber, selectedWeldDetail),
    [tableLimitData, selectedJobNumber, selectedWeldDetail]
  );

  const reportColumns = useMemo(() => {
    const columns = projectReducer?.selectedProject?.reportColumns;
    return columns ? JSON.parse(columns) : { units: {} };
  }, [projectReducer]);

  const { units = {} } = reportColumns;

  const transposedData = useMemo(() => {
    const baseData = [
      {
        key:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? "zone_current"
            : "current",
        label: `Current (${units?.ArcCurrent || "A"})`,
        min: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }current_min`,
        max: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }current_max`,
      },
      {
        key:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? "zone_volts"
            : "volts",
        label: `Volts (${units?.ArcVolts || "V"})`,
        min: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }volts_min`,
        max: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }volts_max`,
      },
      {
        key:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? "zone_wire_speed"
            : "wire_speed",
        label: `Wire Speed (${units?.["Wire Speed"] || "in/min"})`,
        min: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }wire_speed_min`,
        max: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }wire_speed_max`,
      },
      {
        key:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? "zone_travel_speed"
            : "travel_speed",
        label: `Travel Speed (${units?.["Travel Speed"] || "in/min"})`,
        min: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }travel_speed_min`,
        max: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }travel_speed_max`,
      },
      {
        key:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? "zone_oscillation"
            : "oscillation",
        label: `Oscillation Width (${units?.["Oscillation Width"] || "in"})`,
        min: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }oscillation_width_min`,
        max: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }oscillation_width_max`,
      },
      {
        key:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? "zone_calculated_heat"
            : "calculated_heat",
        label: `Heat (${units?.Heat || "KJ/in"})`,
        min: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }calculated_heat_min`,
        max: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }calculated_heat_max`,
      },
      {
        key:
          selectedWeldDetail?.calculation_level === "zone_level"
            ? "zone_heat"
            : "heat",
        label: `True Energy (${units?.["True Energy"] || "KJ/in"})`,
        min: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }heat_min`,
        max: `${
          selectedWeldDetail?.calculation_level === "zone_level" ? "zone_" : ""
        }heat_max`,
      },
    ];
    return baseData;
  }, [units, selectedWeldDetail]);

  const getValue = useCallback(
    (key, type, originalPassName) => {
      if (!tableLimitData || tableLimitData.length === 0 || !selectedJobNumber)
        return null;

      const [jobNum, pipeName] = selectedJobNumber.split(":");

      const filteredData = tableLimitData.filter(
        (item) =>
          (selectedWeldDetail?.calculation_level === "zone_level"
            ? item.zone_name === originalPassName
            : item.pass_group_name === originalPassName) &&
          item.job_number === jobNum &&
          (pipeName
            ? item.pipe_name === pipeName
            : item.pipe_name == null || item.pipe_name == "null")
      );

      const allKeyValues = filteredData
        .filter((item) => item[key])
        .map((item) => Number(item[key]));

      // Determine units and decimal points
      const getUnitAndDecimalPoints = () => {
        const keyMap = {
          current: {
            unit: units?.ArcCurrent || "A",
            key: MEASUREMENT_KEYS.AMPS,
          },
          volts: { unit: units?.ArcVolts || "V", key: MEASUREMENT_KEYS.VOLTS },
          wire_speed: {
            unit: units?.["Wire Speed"] || "in/min",
            key: MEASUREMENT_KEYS.WIRE_SPEED,
          },
          travel_speed: {
            unit: units?.["Travel Speed"] || "in/min",
            key: MEASUREMENT_KEYS.TRAVEL_SPEED,
          },
          oscillation_width: {
            unit: units?.["Oscillation Width"] || "in",
            key: MEASUREMENT_KEYS.OSCILLATE_WIDTH,
          },
          calculated_heat: {
            unit: units?.Heat || "kJ/in",
            key: MEASUREMENT_KEYS.CALCULATED_HEAT,
          },
          heat: {
            unit: units?.["True Energy"] || "kJ/in",
            key: MEASUREMENT_KEYS.HEAT,
          },
        };

        const matchedKey = Object.keys(keyMap).find((k) => key.includes(k));
        return matchedKey ? keyMap[matchedKey] : { unit: "", key: "" };
      };

      const { unit } = getUnitAndDecimalPoints();

      // Calculate min/max values
      if (type === "min" && allKeyValues.length > 0) {
        return convertValue(Math.min(...allKeyValues).toString(), unit, false);
      } else if (type === "max" && allKeyValues.length > 0) {
        return convertValue(Math.max(...allKeyValues).toString(), unit, false);
      }

      return null;
    },
    [tableLimitData, selectedJobNumber, units, selectedWeldDetail]
  );

  const rowsStatusConfigurationAgGrid = useMemo(
    () =>
      transposedData.reduce((acc, ite) => {
        const reducedData = recordList.reduce((accInternal, record) => {
          accInternal[record.pass_group_name] = getValue(
            ite[record.key]?.toString(),
            record.key,
            selectedWeldDetail?.calculation_level === "zone_level"
              ? record?.original_pass_name?.split("-")[1]
              : record?.original_pass_name
          );
          return accInternal;
        }, {});

        acc.push({
          parameter: ite.label,
          ...reducedData,
        });

        return acc;
      }, []),
    [recordList, transposedData, selectedWeldDetail, getValue]
  );

  const columnsAgGrid = useMemo(
    () =>
      recordList.map((ite) => ({
        columnName: ite.pass_group_name,
        value: ite.pass_group_name,
      })),
    [recordList]
  );

  const Loader = StatusData?.isStatusLoading;
  const hasJobNumbers = tableLimitData?.some((item) => item.job_number);
  const uniqueJobNumbers = useMemo(
    () => getUniqueJobNumbers(tableLimitData, weldDetails),
    [tableLimitData, weldDetails]
  );

  return (
    <>
      {Loader ? (
        <div className="slogdata-loader">
          <CommonLoader />
        </div>
      ) : (
        <div className="status-cofig-table">
          {tableLimitData && tableLimitData.length > 0 && hasJobNumbers ? (
            <>
              <div className="status-table-limit">
                <span className="select-job-no">Select Job Number:</span>
                <Select
                  className="job-number-selector"
                  placeholder="Select job number"
                  style={{ width: 200 }}
                  value={selectedJobNumber}
                  onChange={setSelectedJobNumber}
                >
                  {uniqueJobNumbers.map((jobNumber) => (
                    <Option key={jobNumber.value} value={jobNumber.value}>
                      {jobNumber.label}
                    </Option>
                  ))}
                </Select>

                <StatusConfigurationGrid
                  rows={rowsStatusConfigurationAgGrid}
                  columns={columnsAgGrid}
                />
                <div className="input-select-flex">
                  <div className="slop-time">
                    <span className="slop-span">
                      Slope Time (sec):
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;In{" "}
                    </span>
                    <InputNumber
                      value={selectedWeldDetail?.startSkipTime || 0}
                      className="slop-input"
                      disabled={true}
                    />
                    <span className="slop-span"> Out </span>
                    <InputNumber
                      value={selectedWeldDetail?.stopSkipTime || 0}
                      className="slop-input"
                      disabled={true}
                    />
                  </div>
                  <div className="slop-time">
                    <span className="slop-span">
                      Status Calculation Method:{" "}
                    </span>
                    <Select
                      value={selectedProject?.statusCalculationMethod}
                      className="status-selector"
                      disabled={true}
                    >
                      <Option value="Instantaneous">Instantaneous</Option>
                      <Option value="Average Pass">Average Pass</Option>
                      <Option value="Average Zone">Average Zone</Option>
                      <Option value="Average Tilt">Average Tilt</Option>
                    </Select>
                  </div>

                  <div className="slop-time">
                    <span className="slop-span">Calculation Level: </span>
                    <Select
                      value={
                        selectedWeldDetail?.calculation_level || "pass_level"
                      }
                      className="status-selector"
                      disabled={true}
                    >
                      <Option value="pass_level">Pass Level</Option>
                      <Option value="zone_level">Zone Level</Option>
                    </Select>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="slog-NoData">
              <p>
                Status configuration details are not available for this project.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default StatusConfiguration;
